import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Security as SecurityIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useMfaSetupMutation, useMfaVerifyMutation } from '@/store/api/authApi';

const MfaSetupPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [setupMfa, { isLoading: isSettingUp }] = useMfaSetupMutation();
  const [verifyMfa, { isLoading: isVerifying }] = useMfaVerifyMutation();

  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [secret, setSecret] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [verificationCode, setVerificationCode] = useState('');
  const [error, setError] = useState('');
  const [isVerified, setIsVerified] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const initSetup = async () => {
      try {
        const result = await setupMfa().unwrap();
        setQrCodeUrl(result.qrCodeUrl);
        setSecret(result.secret);
        setBackupCodes(result.backupCodes);
      } catch (err) {
        const message =
          (err as { data?: { message?: string } })?.data?.message ||
          t('errors.generic');
        setError(message);
      }
    };

    initSetup();
  }, [setupMfa, t]);

  // Eine Meldung in Worten statt „Kopieren - OK“ (Testbericht TG 2.3) — und
  // wenn der Browser die Zwischenablage verweigert, ein Hinweis statt Stille.
  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(secret);
      setCopied(true);
      enqueueSnackbar('Schlüssel in die Zwischenablage kopiert.', { variant: 'success' });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      enqueueSnackbar('Der Schlüssel ließ sich nicht kopieren. Bitte markieren und von Hand kopieren.', {
        variant: 'warning',
      });
    }
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setVerificationCode(value);
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      await verifyMfa({
        code: verificationCode,
        tempToken: '',
      }).unwrap();
      setIsVerified(true);
      enqueueSnackbar('MFA erfolgreich aktiviert', { variant: 'success' });
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        t('errors.generic');
      setError(message);
      enqueueSnackbar(message, { variant: 'error' });
    }
  };

  const handleFinish = () => {
    navigate('/', { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'light'
            ? 'linear-gradient(135deg, #1a237e 0%, #0d47a1 50%, #01579b 100%)'
            : 'linear-gradient(135deg, #0d1b2a 0%, #1b2838 50%, #1a237e 100%)',
        p: 2,
      }}
    >
      <Card
        sx={{
          maxWidth: 500,
          width: '100%',
          borderRadius: 3,
        }}
        elevation={8}
      >
        <CardContent sx={{ p: 4 }}>
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <SecurityIcon
              sx={{ fontSize: 48, color: 'primary.main', mb: 1 }}
            />
            <Typography variant="h5" fontWeight={700}>
              MFA-Einrichtung
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Sichern Sie Ihr Konto mit Zwei-Faktor-Authentifizierung
            </Typography>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {isSettingUp ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography sx={{ mt: 2 }} color="text.secondary">
                {t('common.loading')}
              </Typography>
            </Box>
          ) : isVerified ? (
            /* Success state */
            <Box sx={{ textAlign: 'center' }}>
              <CheckCircleIcon
                sx={{ fontSize: 64, color: 'success.main', mb: 2 }}
              />
              <Typography variant="h6" gutterBottom>
                MFA erfolgreich aktiviert
              </Typography>

              {backupCodes.length > 0 && (
                <Box sx={{ mt: 2, mb: 3 }}>
                  <Alert severity="warning" sx={{ mb: 2, textAlign: 'left' }}>
                    Speichern Sie diese Backup-Codes sicher ab. Sie werden nur
                    einmal angezeigt.
                  </Alert>
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      backgroundColor: 'grey.50',
                    }}
                  >
                    {backupCodes.map((code, index) => (
                      <Typography
                        key={index}
                        variant="body2"
                        fontFamily="monospace"
                      >
                        {code}
                      </Typography>
                    ))}
                  </Paper>
                </Box>
              )}

              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={handleFinish}
                sx={{ py: 1.5 }}
              >
                Zum Dashboard
              </Button>
            </Box>
          ) : (
            /* Setup form */
            <Box>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* QR Code */}
              <Typography variant="subtitle2" gutterBottom>
                1. Scannen Sie den QR-Code mit Ihrer Authenticator-App
              </Typography>
              {qrCodeUrl && (
                <Box
                  sx={{
                    textAlign: 'center',
                    my: 2,
                    p: 2,
                    backgroundColor: 'white',
                    borderRadius: 2,
                    display: 'inline-block',
                    mx: 'auto',
                    width: '100%',
                  }}
                >
                  <Box
                    component="img"
                    src={qrCodeUrl}
                    alt="MFA QR Code"
                    sx={{
                      width: 200,
                      height: 200,
                      imageRendering: 'pixelated',
                    }}
                  />
                </Box>
              )}

              {/* Manual Secret */}
              <Typography variant="subtitle2" sx={{ mt: 2 }} gutterBottom>
                2. Oder geben Sie diesen Schluessel manuell ein
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 3,
                  fontFamily: 'monospace',
                  backgroundColor: 'grey.50',
                }}
              >
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ wordBreak: 'break-all', mr: 1 }}
                >
                  {secret}
                </Typography>
                <Button
                  size="small"
                  startIcon={copied ? <CheckCircleIcon /> : <CopyIcon />}
                  onClick={handleCopySecret}
                  color={copied ? 'success' : 'primary'}
                >
                  {copied ? 'Kopiert' : t('actions.copy')}
                </Button>
              </Paper>

              {/* Verification */}
              <Typography variant="subtitle2" gutterBottom>
                3. Geben Sie den Verifizierungscode ein
              </Typography>
              <Box component="form" onSubmit={handleVerify} noValidate>
                <TextField
                  fullWidth
                  label={t('auth.mfaCode')}
                  value={verificationCode}
                  onChange={handleCodeChange}
                  required
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SecurityIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                  inputProps={{
                    maxLength: 6,
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                    style: {
                      letterSpacing: '0.5em',
                      fontSize: '1.25rem',
                      textAlign: 'center',
                    },
                  }}
                  placeholder="000000"
                  size="medium"
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={isVerifying || verificationCode.length !== 6}
                  sx={{ py: 1.5 }}
                >
                  {isVerifying ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    t('actions.confirm')
                  )}
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default MfaSetupPage;
